import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../../config/firebase";
import { resolveInstallmentBoletoAttachment } from "./freight-financial-installments";

export const BOLETO_VIEW_LOG_PREFIX = "[BOLETO VIEW]";

export const BOLETO_VIEW_MESSAGES = {
  missingReference: "Não foi possível localizar o arquivo deste boleto.",
  notFound: "O arquivo deste boleto não foi encontrado.",
  unauthorized: "Você não possui permissão para visualizar este boleto.",
  unexpected: "Não foi possível abrir o boleto no momento. Tente novamente.",
  popupBlocked:
    "Não foi possível abrir o boleto. Verifique se o navegador não bloqueou pop-ups.",
};

export function extractStoragePathFromFirebaseUrl(url) {
  if (!url || typeof url !== "string") return "";

  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("firebasestorage.googleapis.com")) return "";

    const marker = "/o/";
    const idx = parsed.pathname.indexOf(marker);
    if (idx < 0) return "";

    const encodedPath = parsed.pathname.slice(idx + marker.length);
    return decodeURIComponent(encodedPath);
  } catch {
    return "";
  }
}

function normalizeStoragePath(value) {
  const path = String(value ?? "").trim();
  if (!path || path === "local") return "";
  return path.startsWith("/") ? path.slice(1) : path;
}

function normalizeHttpUrl(value) {
  const url = String(value ?? "").trim();
  if (!url || url === "local") return "";
  if (!/^https?:\/\//i.test(url)) return "";
  return url;
}

export function resolveBoletoAttachmentForRecord({ record, freightData } = {}) {
  const installmentAttachment = resolveInstallmentBoletoAttachment({
    financialInstallments:
      record?.financialInstallments ?? freightData?.financialInstallments,
    installmentNumber: record?.installmentNumber ?? 1,
    rootBoletoAttachment:
      freightData?.boletoAttachment ??
      freightData?.financial?.boletoAttachment ??
      record?.boletoAttachment ??
      null,
  });

  if (installmentAttachment) return installmentAttachment;

  return (
    record?.boletoAttachment ??
    freightData?.boletoAttachment ??
    freightData?.financial?.boletoAttachment ??
    null
  );
}

export function resolveBoletoViewReference(attachment) {
  if (attachment == null || attachment === "") {
    return { kind: "missing" };
  }

  if (typeof attachment === "string") {
    const trimmed = attachment.trim();
    if (!trimmed || trimmed === "local") {
      return { kind: "missing" };
    }

    const httpUrl = normalizeHttpUrl(trimmed);
    if (httpUrl) {
      const storagePath = extractStoragePathFromFirebaseUrl(httpUrl);
      return {
        kind: storagePath ? "storagePath" : "url",
        storagePath: normalizeStoragePath(storagePath),
        fallbackUrl: httpUrl,
      };
    }

    const storagePath = normalizeStoragePath(trimmed);
    if (storagePath) {
      return { kind: "storagePath", storagePath };
    }

    return { kind: "missing" };
  }

  if (typeof attachment !== "object") {
    return { kind: "missing" };
  }

  const storagePath = normalizeStoragePath(
    attachment.storagePath || attachment.path || attachment.fileId,
  );
  const fallbackUrl = normalizeHttpUrl(
    attachment.downloadUrl || attachment.url || attachment.link,
  );
  const storagePathFromUrl = extractStoragePathFromFirebaseUrl(fallbackUrl);
  const resolvedStoragePath = storagePath || normalizeStoragePath(storagePathFromUrl);

  if (resolvedStoragePath) {
    return {
      kind: "storagePath",
      storagePath: resolvedStoragePath,
      bucket: String(attachment.bucket || "").trim() || null,
      fallbackUrl,
    };
  }

  if (fallbackUrl) {
    return {
      kind: "url",
      fallbackUrl,
    };
  }

  return { kind: "missing" };
}

/**
 * Indica se há referência suficiente para tentar visualizar o boleto.
 * Arquivo local (ainda não enviado) também habilita o botão.
 */
export function canViewBoletoAttachment(attachment, localFile) {
  if (localFile && (localFile.name || typeof localFile === "object")) {
    const localName = String(localFile.name || "").trim();
    if (localName) return true;
  }

  return resolveBoletoViewReference(attachment).kind !== "missing";
}

export async function resolveBoletoViewUrl(reference) {
  if (!reference || reference.kind === "missing") return null;

  // DownloadURL persistida: abrir direto. Não reconsultar o Storage SDK
  // (evita storage/unauthorized quando a URL tokenizada já é válida).
  if (reference.fallbackUrl) {
    return reference.fallbackUrl;
  }

  if (reference.kind === "storagePath" && reference.storagePath) {
    return await getDownloadURL(ref(storage, reference.storagePath));
  }

  return null;
}

export function mapBoletoViewError(error) {
  const code = String(error?.code || error?.error?.code || "").toLowerCase();
  const status = Number(error?.status || error?.error?.status || 0);
  const message = String(error?.message || "").toLowerCase();

  if (
    code.includes("object-not-found") ||
    status === 404 ||
    message.includes("object-not-found") ||
    message.includes("does not exist")
  ) {
    return BOLETO_VIEW_MESSAGES.notFound;
  }

  if (
    code.includes("unauthorized") ||
    code.includes("unauthenticated") ||
    status === 401 ||
    status === 403 ||
    message.includes("permission") ||
    message.includes("unauthorized") ||
    message.includes("unauthenticated")
  ) {
    return BOLETO_VIEW_MESSAGES.unauthorized;
  }

  return BOLETO_VIEW_MESSAGES.unexpected;
}

export function prepareBoletoPreviewWindow() {
  const previewWindow = window.open("", "_blank", "noopener,noreferrer");
  if (!previewWindow) return null;

  previewWindow.opener = null;
  try {
    previewWindow.document.title = "Carregando boleto...";
  } catch {
    // Alguns navegadores bloqueiam acesso ao documento antes da navegação.
  }

  return previewWindow;
}

export function openResolvedBoletoUrl(url, previewWindow) {
  if (!url) return false;

  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.href = url;
    return true;
  }

  // Com "noopener", vários navegadores retornam null mesmo abrindo a aba.
  // Isso não deve ser tratado como bloqueio de pop-up.
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

export function closeBoletoPreviewWindow(previewWindow) {
  if (previewWindow && !previewWindow.closed) {
    previewWindow.close();
  }
}

export function logBoletoView(step, details = {}) {
  const safe = { ...(details || {}) };
  delete safe.url;
  delete safe.downloadUrl;
  delete safe.fallbackUrl;
  delete safe.storagePath;
  delete safe.token;

  if (Object.keys(safe).length === 0) {
    console.info(`${BOLETO_VIEW_LOG_PREFIX} ${step}`);
    return;
  }

  console.info(`${BOLETO_VIEW_LOG_PREFIX} ${step}`, safe);
}

export function logBoletoViewError(step, error, details = {}) {
  const safe = { ...(details || {}) };
  delete safe.url;
  delete safe.downloadUrl;
  delete safe.storagePath;

  const technical = String(
    error?.code || error?.message || (typeof error === "string" ? error : ""),
  ).slice(0, 240);

  console.error(`${BOLETO_VIEW_LOG_PREFIX} ${step}`, {
    ...safe,
    technical,
  });
}

export async function openFreightBoletoDocument({
  record,
  freightData,
  previewWindow,
} = {}) {
  const freightId = record?.freightId || record?.id;

  logBoletoView("Iniciando visualização", { freightId });

  const attachment = resolveBoletoAttachmentForRecord({ record, freightData });
  const reference = resolveBoletoViewReference(attachment);

  if (reference.kind === "missing") {
    return {
      success: false,
      message: BOLETO_VIEW_MESSAGES.missingReference,
      stage: "referencia",
    };
  }

  logBoletoView("Referência encontrada", {
    freightId,
    kind: reference.kind,
    hasPersistedUrl: Boolean(reference.fallbackUrl),
  });

  const source = reference.fallbackUrl ? "downloadUrl" : "storagePath";

  logBoletoView("Recuperando URL do Storage", {
    freightId,
    source,
  });

  const url = await resolveBoletoViewUrl(reference);
  if (!url) {
    return {
      success: false,
      message: BOLETO_VIEW_MESSAGES.missingReference,
      stage: "url",
    };
  }

  logBoletoView("URL recuperada", { freightId, source });
  logBoletoView("Abrindo documento", { freightId });

  const opened = openResolvedBoletoUrl(url, previewWindow);
  if (!opened) {
    return {
      success: false,
      message: BOLETO_VIEW_MESSAGES.popupBlocked,
      stage: "abertura",
    };
  }

  logBoletoView("Visualização concluída", { freightId, source });

  return { success: true, source };
}
